<template>
    <div class="ai-segment-editor">
      <div class="editor-header">
        <h4>
          {{ isEditing ? 'Edit Segment' : 'Create New Segment' }}
          <span v-if="!isEditing" class="insert-position-badge">
            (Inserting {{ insertPosition === 'before' ? 'Before' : 'After' }} Segment #{{ segmentIndex + 1 }})
          </span>
        </h4>
        <div v-if="!isPro" class="pro-badge">PRO feature</div>
      </div>

      <div class="context-info" v-if="currentSegment">
        <p><strong>Speaker:</strong> {{ currentSegment.speaker }} ({{ currentSegment.role }})</p>
        <p v-if="currentSegment.section"><strong>Section:</strong> {{ currentSegment.section }}</p>
      </div>

      <div class="instruction-area">
        <label for="ai-instruction">Instructions for AI:</label>
        <textarea
          id="ai-instruction"
          v-model="instruction"
          placeholder="Tell the AI how to edit or create this segment. E.g.: 'Make this more conversational', 'Add a joke here', 'Transition to the next topic', etc."
          :disabled="isGenerating || isSubmitting || !isPro"
        ></textarea>

        <p class="instruction-tip">Tips: Be specific about tone, content, length. Reference previous or next segments if needed.</p>
      </div>

      <div class="error-message" v-if="errorMessage">{{ errorMessage }}</div>

      <div class="button-row">
        <button
          class="generate-btn"
          @click="generateContent"
          :disabled="isGenerating || isSubmitting || !instruction || !isPro"
        >
          {{ isGenerating ? 'Generating...' : 'Generate with AI' }}
        </button>
        <button
          class="cancel-btn"
          @click="resetContent"
          :disabled="isSubmitting"
        >
          Cancel
        </button>
      </div>

      <div class="generated-content-area" v-if="generatedContent">
        <h5>Generated Content:</h5>
        <div class="content-preview">{{ generatedContent }}</div>
        <div class="action-buttons">
          <button
            class="apply-btn"
            @click="applyGeneratedContent"
            :disabled="isSubmitting"
          >
            {{ isSubmitting ? 'Applying...' : 'Apply This Content' }}
          </button>
          <button
            class="retry-btn"
            @click="generateContent"
            :disabled="isSubmitting || isGenerating"
          >
            {{ isGenerating ? 'Generating...' : 'Regenerate' }}
          </button>
        </div>
      </div>
    </div>
  </template>

  <script>
  import API from '@/api';
  import { mapActions } from 'vuex';

  export default {
    name: 'AISegmentEditor',
    props: {
      messageId: {
        required: true
      },
      segmentIndex: {
        type: Number,
        required: true
      },
      parsedConversation: {
        type: Array,
        required: true
      },
      isEditing: {
        type: Boolean,
        default: false
      },
      isPro: {
        type: Boolean,
        default: false
      },
      insertPosition: {
        type: String,
        default: 'after',
        validator: (value) => ['before', 'after'].includes(value)
      },
      isSubmitting: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        isGenerating: false,
        generatedContent: '',
        originalContent: '',
        errorMessage: '',
        instruction: '',
        selectedSpeaker: '',
        selectedRole: '',
        selectedSection: 'main',
        sectionTypes: [
          { value: 'preview', label: 'Preview', icon: '🔍' },
          { value: 'intro', label: 'Intro', icon: '🎙️' },
          { value: 'main', label: 'Main Content', icon: '📝' },
          { value: 'break', label: 'Sponsor Break', icon: '💰' },
          { value: 'last', label: 'Closing', icon: '🏁' },
          { value: 'discussion', label: 'Discussion', icon: '💬' },
          { value: 'insight', label: 'Insight', icon: '💡' },
          { value: 'resonance', label: 'Resonance', icon: '🔄' },
          { value: 'evolution', label: 'Evolution', icon: '🌱' },
          { value: 'portal', label: 'Portal', icon: '🚪' },
          { value: 'custom', label: 'Custom', icon: '🛠️' }
        ]
      };
    },
    watch: {
        // When inserting position changes, update metadata selections
        insertPosition() {
        if (!this.isEditing) {
            this.initializeSpeakerAndSection();
        }
        },

        // When the segment index changes, update metadata selections
        segmentIndex() {
        if (!this.isEditing) {
            this.initializeSpeakerAndSection();
        }
        }
    },
    created() {
      // Store the original content when component is created
      if (this.segmentIndex >= 0 && this.segmentIndex < this.parsedConversation.length) {
        this.originalContent = this.parsedConversation[this.segmentIndex].content;
      }

      // Set initial values for speaker, role and section when creating a new segment
      if (!this.isEditing && this.segmentIndex >= 0 && this.parsedConversation.length > 0) {
        const referenceSegment = this.parsedConversation[this.segmentIndex];

        if (referenceSegment) {
          // For role, use alternating logic (if reference is host, use expert and vice versa)
          if (referenceSegment.role && referenceSegment.role.toLowerCase().includes('host')) {
            // If reference is host, make this one expert
            const expertSegment = this.parsedConversation.find(s =>
              s.role && !s.role.toLowerCase().includes('host')
            );
            this.selectedSpeaker = expertSegment ? expertSegment.speaker : "Expert";
            this.selectedRole = "Expert";
          } else {
            // If reference is expert or other, make this one host
            const hostSegment = this.parsedConversation.find(s =>
              s.role && s.role.toLowerCase().includes('host')
            );
            this.selectedSpeaker = hostSegment ? hostSegment.speaker : "Host";
            this.selectedRole = "Host";
          }

          // Use the same section as the reference segment
          this.selectedSection = referenceSegment.section || 'main';
        } else {
          this.selectedSpeaker = "Speaker";
          this.selectedRole = "Role";
          this.selectedSection = "main";
        }
      }
    },
    computed: {
      currentSegment() {
        if (this.segmentIndex >= 0 && this.segmentIndex < this.parsedConversation.length) {
          return this.parsedConversation[this.segmentIndex];
        }
        return null;
      },
      previousContext() {
        if (this.segmentIndex > 0) {
          return this.parsedConversation.slice(0, this.segmentIndex);
        }
        return [];
      },
      followingContext() {
        if (this.segmentIndex < this.parsedConversation.length - 1) {
          return this.parsedConversation.slice(this.segmentIndex + 1);
        }
        return [];
      },
      availableSpeakers() {
        // Get unique speakers from existing segments
        const speakers = new Set();
        if (this.parsedConversation && this.parsedConversation.length > 0) {
          this.parsedConversation.forEach(segment => {
            if (segment.speaker) {
              speakers.add(segment.speaker);
            }
          });
        }
        return Array.from(speakers).sort();
      }
    },
    methods: {
      ...mapActions('websocket', ['showTempMessageFn']),

      async generateContent() {
        if (!this.instruction) {
          this.errorMessage = 'Please provide instructions for the AI';
          return;
        }

        this.isGenerating = true;
        this.errorMessage = '';

        try {
          // Prepare the full conversation context for the AI
          const context = {
            previousSegments: this.previousContext,
            currentSegment: this.currentSegment,
            followingSegments: this.followingContext,
            instruction: this.instruction,
            mode: this.isEditing ? 'edit' : 'create'
          };

          // Use the API for request
          const response = await API.post(
            `/user_customization/ai-generate-segment`,
            {
              messageId: this.messageId,
              segmentIndex: this.segmentIndex,
              context: context
            },
            {
              headers: {
                'Content-Type': 'application/json'
              }
            }
          );

          if (response.data) {
            // Check for different response formats
            if (response.data.content) {
              this.generatedContent = response.data.content;
            } else if (response.data.new_content) {
              this.generatedContent = response.data.new_content;
            } else {
              throw new Error('Invalid response format from AI service');
            }
          } else {
            throw new Error('Invalid response from AI service');
          }
        } catch (error) {
          console.error('Error generating content:', error);
          this.errorMessage = error.message || 'An error occurred while generating content';

          this.showTempMessageFn({
            message: "Failed to generate content: " + (error.message || "Unknown error"),
            type: "error",
            timeout: 5000
          });
        } finally {
          this.isGenerating = false;
        }
      },

      applyGeneratedContent() {
        if (!this.generatedContent) {
          this.errorMessage = 'No content has been generated yet';
          return;
        }

        // For new segments, include speaker, role and section data
        if (!this.isEditing) {
          this.$emit('content-updated', {
            index: this.segmentIndex,
            content: this.generatedContent,
            speaker: this.selectedSpeaker,
            role: this.selectedRole,
            section: this.selectedSection
          });
        } else {
          // For editing, just pass the content
          this.$emit('content-updated', {
            index: this.segmentIndex,
            content: this.generatedContent
          });
        }

        // Show message that content is being applied
        this.showTempMessageFn({
          message: "Applying AI-generated content...",
          type: "info",
          timeout: 2000
        });

        // Don't reset content immediately - parent will handle this after submission completes
      },

      resetContent() {
        if (this.isSubmitting) {
          return; // Don't allow cancellation while submitting
        }

        this.generatedContent = '';
        this.instruction = '';
        this.errorMessage = '';

        // Notify user
        this.showTempMessageFn({
          message: "Editing cancelled.",
          type: "info",
          timeout: 2000
        });

        if (this.isEditing) {
          this.$emit('content-updated', {
            index: this.segmentIndex,
            content: this.originalContent
          });
        }

        this.$emit('cancel-edit');
      },

      resetAfterSave() {
        // Reset component state after a successful save
        this.generatedContent = '';
        this.instruction = '';
        this.errorMessage = '';
      }
    }
  };
  </script>

  <style scoped>
  .ai-segment-editor {
    background-color: #f8fafc;
    border-radius: 8px;
    padding: 1rem;
    margin-bottom: 1rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .theme--dark .ai-segment-editor {
    background-color: #1e293b;
  }

  .editor-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }

  .editor-header h4 {
    margin: 0;
    font-size: 1rem;
    color: #334155;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }

  .theme--dark .editor-header h4 {
    color: #e2e8f0;
  }

  .speaker-selection {
    background-color: #f1f5f9;
    padding: 0.75rem;
    border-radius: 6px;
    margin-bottom: 1rem;
    font-size: 0.875rem;
  }

  .theme--dark .speaker-selection {
    background-color: #334155;
  }

  .speaker-row {
    display: flex;
    gap: 1rem;
    margin-bottom: 0.75rem;
  }

  .speaker-field, .role-field, .section-field {
    display: flex;
    flex-direction: column;
  }

  .speaker-field {
    flex: 3;
  }

  .role-field {
    flex: 2;
  }

  .section-field {
    width: 100%;
  }

  .speaker-selection label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #475569;
    font-size: 0.75rem;
  }

  .theme--dark .speaker-selection label {
    color: #cbd5e1;
  }

  .select-input, .text-input {
    padding: 0.5rem;
    border: 1px solid #cbd5e1;
    border-radius: 4px;
    font-size: 0.875rem;
    background-color: white;
  }

  .theme--dark .select-input,
  .theme--dark .text-input {
    background-color: #1e293b;
    border-color: #475569;
    color: #e2e8f0;
  }

  .select-input:disabled,
  .text-input:disabled {
    background-color: #f1f5f9;
    cursor: not-allowed;
    opacity: 0.7;
  }

  .theme--dark .select-input:disabled,
  .theme--dark .text-input:disabled {
    background-color: #0f172a;
  }

  .context-info {
    background-color: #f1f5f9;
    padding: 0.75rem;
    border-radius: 6px;
    margin-bottom: 1rem;
    font-size: 0.875rem;
  }

  .theme--dark .context-info {
    background-color: #334155;
  }

  .context-info p {
    margin: 0.25rem 0;
  }

  .instruction-area {
    margin-bottom: 1rem;
  }

  .instruction-area label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #475569;
  }

  .theme--dark .instruction-area label {
    color: #cbd5e1;
  }

  .instruction-area textarea {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    min-height: 80px;
    resize: vertical;
    font-family: inherit;
  }

  .theme--dark .instruction-area textarea {
    background-color: #1e293b;
    border-color: #475569;
    color: #e2e8f0;
  }

  .instruction-area textarea:disabled {
    background-color: #f1f5f9;
    cursor: not-allowed;
  }

  .theme--dark .instruction-area textarea:disabled {
    background-color: #0f172a;
  }

  .instruction-tip {
    font-size: 0.75rem;
    color: #64748b;
    margin-top: 0.25rem;
  }

  .theme--dark .instruction-tip {
    color: #94a3b8;
  }

  .error-message {
    color: #ef4444;
    margin-bottom: 1rem;
    padding: 0.5rem;
    background-color: #fee2e2;
    border-radius: 4px;
    font-size: 0.875rem;
  }

  .theme--dark .error-message {
    background-color: rgba(239, 68, 68, 0.2);
  }

  .button-row {
    display: flex;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .generate-btn, .cancel-btn, .apply-btn, .retry-btn {
    padding: 0.5rem 1rem;
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .generate-btn {
    background-color: #3b82f6;
    color: white;
    border: none;
  }

  .generate-btn:hover:not(:disabled) {
    background-color: #2563eb;
  }

  .generate-btn:disabled {
    background-color: #cbd5e1;
    cursor: not-allowed;
  }

  .cancel-btn {
    background-color: transparent;
    border: 1px solid #cbd5e1;
    color: #64748b;
  }

  .cancel-btn:hover:not(:disabled) {
    background-color: #f1f5f9;
    color: #475569;
  }

  .cancel-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .theme--dark .cancel-btn {
    border-color: #475569;
    color: #94a3b8;
  }

  .theme--dark .cancel-btn:hover:not(:disabled) {
    background-color: #334155;
    color: #e2e8f0;
  }

  .generated-content-area {
    margin-top: 1.5rem;
    padding: 1rem;
    background-color: white;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
  }

  .theme--dark .generated-content-area {
    background-color: #1e293b;
    border-color: #334155;
  }

  .generated-content-area h5 {
    margin-top: 0;
    margin-bottom: 0.75rem;
    font-size: 0.875rem;
    color: #475569;
  }

  .theme--dark .generated-content-area h5 {
    color: #cbd5e1;
  }

  .content-preview {
    padding: 0.75rem;
    background-color: #f8fafc;
    border-radius: 4px;
    margin-bottom: 1rem;
    white-space: pre-wrap;
  }

  .theme--dark .content-preview {
    background-color: #0f172a;
  }

  .action-buttons {
    display: flex;
    gap: 0.75rem;
  }

  .apply-btn {
    background-color: #10b981;
    color: white;
    border: none;
    flex: 1;
  }

  .apply-btn:hover:not(:disabled) {
    background-color: #059669;
  }

  .apply-btn:disabled {
    background-color: #7dd3b3;
    cursor: not-allowed;
  }

  .retry-btn {
    background-color: #f8fafc;
    border: 1px solid #cbd5e1;
    color: #64748b;
  }

  .retry-btn:hover:not(:disabled) {
    background-color: #f1f5f9;
    color: #475569;
  }

  .retry-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .theme--dark .retry-btn {
    background-color: #1e293b;
    border-color: #475569;
    color: #94a3b8;
  }

  .theme--dark .retry-btn:hover:not(:disabled) {
    background-color: #334155;
    color: #e2e8f0;
  }

  @media (max-width: 768px) {
    .button-row, .action-buttons {
      flex-direction: column;
      gap: 0.5rem;
    }
  }

  .pro-badge {
    background-color: #8b5cf6;
    color: white;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
  }

  .insert-position-badge {
    font-size: 0.75rem;
    font-weight: normal;
    color: #64748b;
    margin-left: 0.5rem;
    background-color: #e2e8f0;
    padding: 0.125rem 0.375rem;
    border-radius: 3px;
  }

  .insert-position-badge.before {
    border-left: 2px solid #3b82f6;
    color: #3b82f6;
  }

  .insert-position-badge.after {
    border-left: 2px solid #10b981;
    color: #10b981;
  }

  .theme--dark .insert-position-badge {
    color: #94a3b8;
    background-color: #334155;
  }

  .theme--dark .insert-position-badge.before {
    color: #60a5fa;
  }

  .theme--dark .insert-position-badge.after {
    color: #34d399;
  }
  </style>
